import React, { useState, useEffect, useRef } from 'react';
import './SendVelvynoteModal.css';

const MAX_MESSAGE_LENGTH = 2000;

/**
 * Modal for composing a velvynote message
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether modal is visible
 * @param {Function} props.onClose - Handler to close modal
 * @param {Object} props.recipient - Recipient profile (handle, country, etc.)
 * @param {number} props.totalMessages - User's total available messages
 * @param {Function} props.onSend - Handler when message is sent (message) => Promise
 * @param {boolean} props.loading - Whether sending is in progress
 */
export default function SendVelvynoteModal({
  isOpen,
  onClose,
  recipient,
  totalMessages = 0,
  onSend,
  loading = false,
}) {
  const [message, setMessage] = useState('');
  const [error, setError] = useState(null);
  const textareaRef = useRef(null);

  // Focus textarea when modal opens
  useEffect(() => {
    if (isOpen && textareaRef.current) {
      setTimeout(() => textareaRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Reset state when modal opens
  useEffect(() => {
    if (isOpen) {
      setMessage('');
      setError(null);
    }
  }, [isOpen]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && !loading) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, loading, onClose]);

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !loading) {
      onClose();
    }
  };

  const handleSend = async () => {
    if (!message.trim() || loading) return;

    if (message.length > MAX_MESSAGE_LENGTH) {
      setError(`Message too long (max ${MAX_MESSAGE_LENGTH} characters)`);
      return;
    }

    if (totalMessages <= 0) {
      setError('No messages available');
      return;
    }

    setError(null);

    try {
      await onSend(message.trim());
    } catch (err) {
      setError(err.message || 'Failed to send');
    }
  };

  const remainingChars = MAX_MESSAGE_LENGTH - message.length;
  const isOverLimit = remainingChars < 0;
  const canSend = message.trim().length > 0 && !isOverLimit && totalMessages > 0 && !loading;

  if (!isOpen) return null;

  return (
    <div className="send-velvynote-overlay" onClick={handleOverlayClick}>
      <div className="send-velvynote-modal">
        <button
          className="send-velvynote-close"
          onClick={onClose}
          disabled={loading}
          aria-label="Close"
        >
          <span aria-hidden="true">&times;</span>
        </button>

        <div className="send-velvynote-header">
          <span className="send-velvynote-icon">&#128140;</span>
          <h2>Send a Velvynote</h2>
          <p className="send-velvynote-recipient">
            to <strong>@{recipient?.handle}</strong>
          </p>
        </div>

        {error && (
          <div className="send-velvynote-error">
            {error}
          </div>
        )}

        <div className="send-velvynote-content">
          <div className="letter-paper">
            <textarea
              ref={textareaRef}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Dear stranger, your memory caught my attention..."
              className="letter-textarea"
              disabled={loading}
              maxLength={MAX_MESSAGE_LENGTH + 50}
            />
            <div className={`letter-char-count ${isOverLimit ? 'over-limit' : remainingChars < 100 ? 'warning' : ''}`}>
              {remainingChars}
            </div>
          </div>
        </div>

        <div className="send-velvynote-footer">
          <div className="send-velvynote-messages">
            <span className="messages-icon" aria-hidden>
              📧
            </span>
            <span className="messages-text">
              {totalMessages > 0 ? (
                <>Sending costs <strong>1 message</strong> ({totalMessages} available)</>
              ) : (
                <span className="no-messages">No messages available</span>
              )}
            </span>
          </div>

          <div className="send-velvynote-actions">
            <button
              className="send-velvynote-cancel"
              onClick={onClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              className="send-velvynote-send"
              onClick={handleSend}
              disabled={!canSend}
            >
              {loading ? (
                <>
                  <span className="send-velvynote-spinner"></span>
                  Sending...
                </>
              ) : (
                <>
                  <span>&#128140;</span>
                  Send Velvynote
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
